/**
 * BookCard — the hero of the bookshelf: an illustrated cover with
 * minimal metadata, a lift-on-hover interaction, and quiet actions.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Heart, MoreVertical, Check, BookmarkPlus, BookOpen, CheckCircle2, Trash2, Eye } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { cx, Stars } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useBookshelf } from './context/BookshelfContext';
import { getBookProgress, getStatusLabel } from './utils/bookHelpers';
import BookCoverArt from '@/components/books/BookCoverArt';
import { BOOKSHELF } from '@/constants/testIds';

const STATUS_TONE = {
  tbr: 'bg-blue/20 text-[#4d616c] dark:text-[#aabecb]',
  reading: 'bg-coral/12 text-coral',
  finished: 'bg-sage/40 text-[#4f6249] dark:bg-sage/20 dark:text-[#b3c7aa]',
};

const STATUS_ACTIONS = [
  { status: 'tbr', label: 'Mark as TBR', icon: BookmarkPlus },
  { status: 'reading', label: 'Mark as Reading', icon: BookOpen },
  { status: 'finished', label: 'Mark as Finished', icon: CheckCircle2 },
];

export default function BookCard({ book, onOpenDetails }) {
  const { shelves, updateBook, moveBook, removeBook, toggleFavorite } = useBookshelf();
  const reduceMotion = useReducedMotion();
  const progress = getBookProgress(book);
  const customShelves = shelves.filter(s => s.type === 'custom');

  const handleStatus = (status) => {
    updateBook(book.id, { status });
    toast(`Marked “${book.title}” as ${getStatusLabel(status)}`);
  };
  const handleMove = (shelf) => {
    moveBook(book.id, shelf.id);
    toast(`Moved “${book.title}” to ${shelf.name}`);
  };
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    toggleFavorite(book.id);
  };
  const handleRemove = () => {
    removeBook(book.id);
    toast(`Removed “${book.title}” from your bookshelf`);
  };

  return (
    <motion.article layout="position" className="group relative" data-testid={BOOKSHELF.bookCard}>
      <motion.button
        type="button"
        onClick={() => onOpenDetails(book)}
        whileHover={reduceMotion ? undefined : { y: -10, rotate: -1.1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 22 }}
        className="block w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
        aria-label={`${book.title} by ${book.author}, ${getStatusLabel(book.status)}${book.status === 'reading' ? `, ${progress}% read` : ''}`}
      >
        <div className="relative">
          <BookCoverArt
            book={book}
            className="aspect-[2/3] shadow-card transition-shadow duration-300 group-hover:shadow-lift"
          />

          {book.status === 'reading' && (
            <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden rounded-b-md bg-black/20">
              <div className="h-full bg-gradient-to-r from-coral to-yellow" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </motion.button>

      <span
        role="button"
        tabIndex={0}
        onClick={handleFavoriteClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFavoriteClick(e); } }}
        className={cx(
          'absolute left-2 top-2 grid h-7 w-7 cursor-pointer place-items-center rounded-full bg-black/30 backdrop-blur-sm transition-opacity',
          book.isFavorite ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
        )}
        aria-label={book.isFavorite ? `Remove ${book.title} from favorites` : `Add ${book.title} to favorites`}
      >
        <Heart className={cx('h-3.5 w-3.5', book.isFavorite ? 'fill-coral text-coral' : 'text-white')} />
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/30 text-white opacity-0 backdrop-blur-sm transition-opacity focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
            aria-label={`Options for ${book.title}`}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-xl border-line bg-card p-1.5 text-ink shadow-xl">
          <DropdownMenuItem onSelect={() => onOpenDetails(book)} className="cursor-pointer gap-2.5 rounded-lg text-sm">
            <Eye className="h-3.5 w-3.5 text-subtle" /> View Details
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-line" />
          {STATUS_ACTIONS.map(({ status, label, icon: Icon }) => (
            <DropdownMenuItem key={status} onSelect={() => handleStatus(status)} className="cursor-pointer gap-2.5 rounded-lg text-sm">
              <Icon className="h-3.5 w-3.5 text-subtle" /> {label}
              {book.status === status && <Check className="ml-auto h-3.5 w-3.5 text-coral" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem onSelect={() => toggleFavorite(book.id)} className="cursor-pointer gap-2.5 rounded-lg text-sm">
            <Heart className={cx('h-3.5 w-3.5', book.isFavorite ? 'fill-coral text-coral' : 'text-subtle')} />
            {book.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </DropdownMenuItem>

          {customShelves.length > 0 && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer gap-2.5 rounded-lg text-sm">
                <BookmarkPlus className="h-3.5 w-3.5 text-subtle" /> Move to Shelf
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="w-48 rounded-xl border-line bg-card p-1.5 text-ink shadow-xl">
                  {customShelves.map(shelf => (
                    <DropdownMenuItem key={shelf.id} onSelect={() => handleMove(shelf)} className="cursor-pointer gap-2 rounded-lg text-sm">
                      {shelf.emoji ? <span>{shelf.emoji}</span> : <BookmarkPlus className="h-3.5 w-3.5 text-subtle" />}
                      {shelf.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          )}

          <DropdownMenuSeparator className="bg-line" />
          <DropdownMenuItem onSelect={handleRemove} className="cursor-pointer gap-2.5 rounded-lg text-sm font-medium text-coral focus:bg-coral/10 focus:text-coral">
            <Trash2 className="h-3.5 w-3.5" /> Remove from Bookshelf
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="mt-2.5 px-0.5">
        <h3 className="line-clamp-2 font-display text-[13.5px] font-semibold leading-snug text-ink">{book.title}</h3>
        <p className="mt-0.5 truncate text-xs text-subtle">{book.author}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className={cx('rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_TONE[book.status])}>
            {getStatusLabel(book.status)}
          </span>
          {book.rating && <Stars value={Math.round(book.rating)} size={10} />}
        </div>
        {book.status === 'reading' && <p className="mt-1 text-[11px] font-medium text-subtle">{progress}% read</p>}
        {book.friendsReading > 0 && (
          <p className="mt-1 truncate text-[10.5px] text-subtle/80">
            {book.friendsReading} friend{book.friendsReading > 1 ? 's are' : ' is'} reading this
          </p>
        )}
      </div>
    </motion.article>
  );
}
